import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { Dialog } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';

import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectFileUploadPanel } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadPanel';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const useProjectFileUpload = (fileId?: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectFileUploadSelector(projectId, fileId));
};

const ProjectFileUploadPreviewDialogBody: React.VFC = () => {
  const { resourceId } = useReduxSelector(
    (state) => state.ui.fileUploadPreviewDialog
  );

  const fileObj = useProjectFileUpload(resourceId);

  return fileObj ? (
    <ProjectFileUploadPanel
      disableEditing
      existingFields={fileObj.fields}
      file={fileObj.contents}
      fileName={fileObj.name}
      fileOptions={fileObj.options}
      filePath={fileObj.path}
      isFilePartial={fileObj.partial}
      setErrorText={noop}
      setFields={noop}
      setFileName={noop}
      setFileOptions={noop}
      setRows={noop}
    />
  ) : null;
};

export const ProjectFileUploadPreviewDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { isOpen } = useReduxSelector(
    (state) => state.ui.fileUploadPreviewDialog
  );

  const handleToggleDialog = React.useCallback(
    (isOpen: boolean) =>
      dispatch(uiSlice.actions.toggledFileUploadPreviewDialog({ isOpen })),
    [dispatch]
  );

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-project-resource', 'sd-dialog-large')}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={() => handleToggleDialog(false)}
      title={copy.components.fileUploadPreviewDialog.title}
    >
      <div className={Classes.DIALOG_BODY}>
        <Route path={ROUTE.PROJECT}>
          <ProjectFileUploadPreviewDialogBody />
        </Route>
      </div>
    </Dialog>
  );
};
